/**
 * Display formatting for the Finanzen tab. No selection, no arithmetic.
 *
 * `formatCurrency` is the repo's CHF formatter and is reused verbatim rather
 * than re-implemented — but it maps null/undefined to "CHF 0"
 * (`Number(null || 0)`), which is exactly the claim a missing figure must not
 * make. So the null decision happens HERE, before formatting, and the card
 * components render "—" for it.
 *
 * `chf` is the default for every franc figure on this surface, headline and
 * breakdown alike. A breakdown shown to the cent under a headline rounded to
 * the franc would not visibly add up, and a reader cannot be expected to know
 * why.
 *
 * `chfPrecise` is the deliberate exception, for figures that are not part of
 * that add-up and whose centimes carry information — an annual insurance
 * premium is a real recurring cost, and CHF 1'499.35 rounded to CHF 1'499
 * quietly drops 35 Rappen from a number an operator reconciles against an
 * invoice. It delegates to the repo's existing Swiss two-decimal formatter
 * rather than defining a third money world.
 */
import { fmtMoney } from "../bexio/bexioFormat";
import { formatCurrency } from "../../../pages/admin/uebersicht/uebersichtHelpers";

/** A franc amount, or `null` when there is no figure to state. */
export const chf = (value) =>
  value === null || value === undefined ? null : formatCurrency(value);

/**
 * A whole count as de-CH text, or `null`.
 *
 * Zero is a real answer here and is returned as "0": "no open invoices" is
 * information, unlike a zero franc figure standing in for a failed request.
 */
export const countText = (value) =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? null
    : new Intl.NumberFormat("de-CH").format(Math.round(Number(value)));

/**
 * A share as de-CH percent text, or `null`.
 *
 * Takes a PERCENTAGE (0..100), not a ratio — the same unit the contribution
 * margin already uses on this surface, so one reading of "a number that ends
 * in %" holds across the tab. A non-finite input yields null rather than
 * "NaN %": a quota that could not be computed is not a quota of zero.
 */
export const percent = (value) =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? null
    : `${new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 }).format(Number(value))} %`;

/**
 * A franc amount to the centime, or `null` when there is no figure.
 *
 * `fmtMoney` is the repo's Swiss two-decimal formatter and is already used on
 * this surface for the unclassified cost remainder (`FinanceKpiGrid`), so this
 * is a reuse, not a new convention. Its own null handling returns "—"; the null
 * decision still happens HERE so both formatters behave identically at the call
 * site and the card components keep deciding what an absent figure looks like.
 */
export const chfPrecise = (value) =>
  value === null || value === undefined ? null : fmtMoney(value);
